import {
  Badge,
  Button,
  Callout,
  Card,
  ConfirmDialog,
  LoadingRegion,
  Select,
  Skeleton,
  useToast,
} from '@apovisio/ui';
import type { MeetingDetail } from '@apovisio/contracts';
import { ArrowLeft, Pencil, Video, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { PageHeading } from '../../components/PageHeading';
import {
  useAddCoHost,
  useCloseMeeting,
  useDeleteMeeting,
  useMeeting,
  useRemoveCoHost,
  useUsers,
} from '../../features/meetings/api';
import { MeetingStatusBadge } from '../../features/meetings/badges';
import { InvitationsCard } from '../../features/meetings/InvitationsCard';
import { WaitingRoomCard } from '../../features/meetings/WaitingRoomCard';
import { ApiClientError } from '../../lib/api';
import { formatDateTime, formatSlot } from '../../lib/format';

/** Écran 5 — Détail de réunion : co-hôtes, invitations, statut, actions. */
export function MeetingDetailPage() {
  const { id = '' } = useParams();
  const meeting = useMeeting(id);

  if (meeting.isPending) {
    return (
      <LoadingRegion label="Chargement de la visio">
        <div className="av-stack">
          <Skeleton width="50%" height={28} />
          <Skeleton width="30%" height={16} />
        </div>
      </LoadingRegion>
    );
  }
  if (!meeting.data) {
    const notFound = meeting.error instanceof ApiClientError && meeting.error.status === 404;
    return (
      <div className="av-stack">
        <Link to="/admin" className="app-back">
          <ArrowLeft className="av-icon" aria-hidden="true" />
          Toutes les visios
        </Link>
        <Callout
          tone="danger"
          title={notFound ? 'Visio introuvable' : 'Impossible de charger la visio'}
          live="alert"
        />
      </div>
    );
  }
  return <MeetingDetailView meeting={meeting.data} />;
}

function MeetingDetailView({ meeting }: { meeting: MeetingDetail }) {
  const navigate = useNavigate();
  const toast = useToast();
  const close = useCloseMeeting(meeting.id);
  const remove = useDeleteMeeting(meeting.id);
  const [confirm, setConfirm] = useState<'close' | 'delete' | null>(null);
  const ended = meeting.status === 'ENDED';
  const canManage = meeting.myRole !== 'CO_HOST';

  const onError = (title: string) => (error: unknown) => {
    toast.show({
      tone: 'danger',
      title,
      description: error instanceof ApiClientError ? error.message : undefined,
    });
  };

  return (
    <div className="av-stack av-stack--lg">
      <div className="av-stack">
        <Link to="/admin" className="app-back">
          <ArrowLeft className="av-icon" aria-hidden="true" />
          Toutes les visios
        </Link>
        <div className="av-row av-row--between">
          <div className="av-stack av-stack--sm">
            <PageHeading documentTitle={meeting.title}>{meeting.title}</PageHeading>
            <div className="av-row">
              <MeetingStatusBadge status={meeting.status} />
              <span className="av-text-secondary av-text-sm">
                {formatSlot(meeting.scheduledStart, meeting.scheduledEnd)} · expire le{' '}
                {formatDateTime(meeting.expiresAt)}
              </span>
            </div>
          </div>
          {!ended ? (
            <div className="av-row">
              {canManage ? (
                <Button
                  variant="secondary"
                  icon={<Pencil />}
                  onClick={() => {
                    void navigate(`/admin/reunions/${meeting.id}/modifier`);
                  }}
                >
                  Modifier
                </Button>
              ) : null}
              <Button
                icon={<Video />}
                onClick={() => {
                  void navigate(`/admin/reunions/${meeting.id}/salle`);
                }}
              >
                {meeting.status === 'LIVE' ? 'Rejoindre la visio' : 'Démarrer la visio'}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {ended ? (
        <Callout tone="info" title="Cette visio est terminée.">
          Les liens ne fonctionnent plus et personne ne peut être réadmis.
        </Callout>
      ) : null}

      <div className="app-two-columns">
        <div className="av-stack av-stack--lg">
          {ended ? null : <WaitingRoomCard meetingId={meeting.id} />}
          <InvitationsCard meeting={meeting} />
        </div>
        <div className="av-stack av-stack--lg">
          <CoHostsCard meeting={meeting} canManage={canManage && !ended} />
          <Card title="Options">
            <ul className="app-list">
              <li>
                <span>Chat</span>
                {meeting.chatEnabled ? (
                  <Badge tone="success">Activé</Badge>
                ) : (
                  <Badge>Désactivé</Badge>
                )}
              </li>
              <li>
                <span>Partage d’écran</span>
                {meeting.screenShareEnabled ? (
                  <Badge tone="success">Activé</Badge>
                ) : (
                  <Badge>Désactivé</Badge>
                )}
              </li>
            </ul>
          </Card>
          <div className="av-stack av-stack--sm">
            {!ended ? (
              <div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setConfirm('close');
                  }}
                >
                  Terminer la visio pour tous…
                </Button>
              </div>
            ) : null}
            {canManage ? (
              <div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setConfirm('delete');
                  }}
                >
                  Supprimer la visio…
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirm === 'close'}
        title="Terminer la visio pour tous ?"
        description="Tous les participants seront déconnectés, les liens cesseront de fonctionner et personne ne pourra être réadmis."
        confirmLabel="Terminer pour tous"
        busy={close.isPending}
        onCancel={() => {
          setConfirm(null);
        }}
        onConfirm={() => {
          close.mutate(undefined, {
            onSuccess: () => {
              setConfirm(null);
              toast.show({ tone: 'success', title: 'Visio terminée' });
            },
            onError: onError('Impossible de terminer la visio'),
          });
        }}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        title={`Supprimer « ${meeting.title} » ?`}
        description="La visio disparaît de vos listes et ses liens cessent de fonctionner. Le journal d’audit est conservé."
        confirmLabel="Supprimer"
        busy={remove.isPending}
        onCancel={() => {
          setConfirm(null);
        }}
        onConfirm={() => {
          remove.mutate(undefined, {
            onSuccess: () => {
              toast.show({ tone: 'success', title: 'Visio supprimée' });
              void navigate('/admin', { replace: true });
            },
            onError: onError('Suppression impossible'),
          });
        }}
      />
    </div>
  );
}

function CoHostsCard({ meeting, canManage }: { meeting: MeetingDetail; canManage: boolean }) {
  const toast = useToast();
  const users = useUsers();
  const add = useAddCoHost(meeting.id);
  const remove = useRemoveCoHost(meeting.id);
  const candidates = (users.data ?? []).filter(
    (user) => user.id !== meeting.owner.id && !meeting.coHosts.some((c) => c.id === user.id),
  );
  const onError = (error: unknown) => {
    toast.show({
      tone: 'danger',
      title: 'Modification impossible',
      description: error instanceof ApiClientError ? error.message : undefined,
    });
  };

  return (
    <Card title="Co-hôtes" description="Ils peuvent admettre, refuser et révoquer.">
      <ul className="app-list">
        <li>
          <span>{meeting.owner.displayName}</span>
          <Badge tone="info">Propriétaire</Badge>
        </li>
        {meeting.coHosts.map((coHost) => (
          <li key={coHost.id}>
            <span>{coHost.displayName}</span>
            {canManage ? (
              <Button
                variant="ghost"
                icon={<X />}
                aria-label={`Retirer ${coHost.displayName}`}
                loading={remove.isPending && remove.variables === coHost.id}
                onClick={() => {
                  remove.mutate(coHost.id, { onError });
                }}
              >
                Retirer
              </Button>
            ) : (
              <Badge>Co-hôte</Badge>
            )}
          </li>
        ))}
      </ul>
      {canManage ? (
        <Select
          label="Ajouter un co-hôte"
          value=""
          disabled={candidates.length === 0 || add.isPending}
          hint={
            candidates.length === 0
              ? 'Seuls les membres Apogée déjà connectés une fois apparaissent ici.'
              : undefined
          }
          onChange={(event) => {
            if (event.target.value) add.mutate(event.target.value, { onError });
          }}
        >
          <option value="">Choisir un membre Apogée</option>
          {candidates.map((user) => (
            <option key={user.id} value={user.id}>
              {user.displayName} ({user.email})
            </option>
          ))}
        </Select>
      ) : null}
    </Card>
  );
}
